import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams } from 'react-router'
import ConversationView from '../components/chat/ConversationView'
import { NEW_CHAT_SETTINGS, useChat } from '../providers/chat/context'
import HomeHero from '../components/home/HomeHero'
import PresetPrompts from '../components/home/PresetPrompts'
import PromptComposer from '../components/home/PromptComposer'
import { useComposerAttachments } from '../components/home/useComposerAttachments'

export default function Home() {
  const { activeId, messages, loadingMessages, busy, balance, send, openThread, newThread } = useChat()
  const { threadId = null } = useParams()
  const navigate = useNavigate()
  const synced = useRef<{ threadId?: string | null; activeId?: string | null }>({})
  const [prompt, setPrompt] = useState('')
  const [settings, setSettings] = useState(NEW_CHAT_SETTINGS)
  const outOfCredits = balance !== null && balance.credits_remaining <= 0
  const uploads = useComposerAttachments(outOfCredits)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // An empty thread still shows the hero, so "New chat" always lands back here.
  const started = threadId !== null || messages.length > 0 || busy !== null || loadingMessages

  useEffect(() => {
    const last = synced.current
    synced.current = { threadId, activeId }
    if (threadId !== last.threadId) {
      if (threadId === activeId) return
      if (threadId) void openThread(threadId)
      else newThread()
    } else if (activeId !== last.activeId && activeId !== threadId) {
      void navigate(activeId ? `/c/${activeId}` : '/', { replace: true })
    }
  }, [threadId, activeId, openThread, newThread, navigate])

  useEffect(() => {
    if (started || window.matchMedia('(pointer: coarse)').matches) return
    textareaRef.current?.focus()
  }, [started])

  if (started) return <ConversationView />

  function selectPreset(preset: string) {
    flushSync(() => setPrompt(preset))
    textareaRef.current?.focus()
    textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  function submit() {
    const message = prompt.trim()
    if (!message && uploads.attachments.length === 0) return

    const sent = uploads.attachments
    setPrompt('')
    uploads.clear()
    void send(message, sent, settings)
    setSettings(NEW_CHAT_SETTINGS)
  }

  return (
    <div className="flex flex-1 flex-col bg-white">
      <HomeHero />
      <div className="relative -mt-16 mb-10 px-6">
        <PromptComposer
          value={prompt}
          onChange={setPrompt}
          uploads={uploads}
          textareaRef={textareaRef}
          onSubmit={submit}
          settings={settings}
          onSettingsChange={setSettings}
          disabled={outOfCredits}
          placeholder={
            outOfCredits ? 'Out of ad credits — top up to continue.' : 'Ask a question or provide a task...'
          }
        />
      </div>
      <PresetPrompts onSelect={selectPreset} />
    </div>
  )
}
